import React, { useState } from "react";
import * as merchantService from "@/shared/api/services/merchantService";
import * as transactionService from "@/shared/api/services/transactionService";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { getDateRange } from "@/shared/utils/dateFilters";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Clock,
  XCircle,
  Wallet,
  RefreshCw,
} from "lucide-react";
import AreaStatsChart from "@/shared/components/dashboard/TransactionChart";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import StatCard from "@/shared/components/merchant/StatCard";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { Button } from "@/shared/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import utilities from "@/shared/utils/utilities";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

export default function MerchantDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  // Queries
  const { data: profile, refetch: refetchProfile } = useQuery({
    queryKey: ["merchant-profile"],
    queryFn: () => merchantService.getProfile(),
  });

  // Compute date range from filter state
  const { startDate, endDate } = React.useMemo(
    () => getDateRange(dateFilter, customDateRange),
    [dateFilter, customDateRange],
  );
  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  // Dashboard stats
  const {
    data: dashboardStats,
    isLoading: isStatsLoading,
    refetch: refetchDashboardStats,
  } = useQuery({
    queryKey: ["dashboard-stats", profile?.id, startISO, endISO],
    queryFn: () =>
      merchantService.getDashboardStats(profile?.id, startISO, endISO),
    enabled: !!profile?.id,
  });

  // Recent transactions (5 most recent)
  const {
    data: recentTransactionsResult,
    isPending: isRecentLoading,
    refetch: refetchRecentTransactions,
  } = useQuery({
    queryKey: ["merchant-recent-transactions", profile?.id],
    queryFn: () => transactionService.getPaginatedTransactions(1, 5, "", {}, profile?.id ? [profile.id] : []),
    enabled: !!profile?.id,
  });
  const recentTransactions = recentTransactionsResult?.data || [];

  // Transactions chart (grouped deposits/withdrawals time series)
  const { data: chartDataResult, refetch: refetchChart } = useQuery({
    queryKey: ["merchant-transactions-chart", profile?.id, startISO, profile?.currency, endISO],
    queryFn: () =>
      merchantService.getTransactionsChart(startISO, endISO, profile?.currency, profile?.id ? [profile.id] : null),
    enabled: !!profile?.id,
    placeholderData: (prev) => prev,
  });
  const chartData = chartDataResult || [];

  const currencySymbol = utilities.getCurrencySymbol(profile?.currency);

  const handleRefresh = () => {
    setIsRefreshing(true);
    Promise.all([
      refetchProfile(),
      refetchDashboardStats(),
      refetchRecentTransactions(),
      refetchChart(),
    ]).finally(() => setIsRefreshing(false));
  };

  const statCards = [
    {
      title: t("balance"),
      value: `${currencySymbol}${dashboardStats?.currentBalance || "0"}`,
      subValue: profile?.currency,
      icon: Wallet,
      iconColor: "bg-violet-500",
    },
    {
      title: t("totalDeposits"),
      value: `${currencySymbol}${dashboardStats?.totalDeposits ?? 0}`,
      icon: ArrowDownToLine,
      iconColor: "bg-blue-500",
    },
    {
      title: t("totalWithdrawals"),
      value: `${currencySymbol}${dashboardStats?.totalWithdrawals ?? 0}`,
      icon: ArrowUpFromLine,
      iconColor: "bg-emerald-500",
    },
    {
      title: t("successful"),
      value: dashboardStats?.successfulTxCount ?? 0,
      icon: CheckCircle2,
      iconColor: "bg-emerald-500",
    },
    {
      title: t("pending"),
      value: dashboardStats?.pendingTxCount ?? 0,
      icon: Clock,
      iconColor: "bg-amber-500",
    },
    {
      title: t("declined"),
      value: dashboardStats?.declinedTxCount ?? 0,
      icon: XCircle,
      iconColor: "bg-red-500",
    },
  ];

  return (
    <MerchantLayout currentPage="MerchantDashboard">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              {t("dashboard")}
            </h1>
            <p className="text-slate-500">{t("welcomeBackOverview")}</p>
          </div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <DateFilter
              value={dateFilter}
              onChange={setDateFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <Button
              variant="outline"
              size="icon"
              className="bg-white w-full sm:w-10"
              onClick={handleRefresh}
            >
              <RefreshCw
                className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
            </Button>
            <Link to={createPageUrl("MerchantDeposit")}>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                <ArrowDownToLine className="h-4 w-4" />
                {t("newDeposit")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {isStatsLoading || !dashboardStats
            ? Array.from({ length: 6 }).map((_, i) => (
              <Skeleton
                key={i}
                height="100%"
                width="100%"
                borderRadius="1rem"
                containerClassName="block h-32 w-full"
              />
            ))
            : statCards.map((stat, i) => (
              <StatCard
                key={stat.title}
                title={stat.title}
                value={stat.value}
                subValue={stat.subValue}
                icon={stat.icon}
                iconColor={stat.iconColor}
                delay={i * 0.05}
              />
            ))}
        </div>

        {/* Chart */}
        <AreaStatsChart
          data={chartData}
          currency={currencySymbol}
          title="Transaction Volume"
        />
        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 border border-slate-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              {t("recentTransactions")}
            </h3>
            <Link to={createPageUrl("MerchantTransactions")}>
              <Button
                variant="ghost"
                className="text-blue-600 hover:text-blue-700"
              >
                {t("viewAll")}
              </Button>
            </Link>
          </div>

          <TransactionTable
            transactions={recentTransactions}
            isLoading={isRecentLoading}
            onRowClick={setSelectedTx}
          />
        </motion.div>
      </div>

      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        defaultCurrency={selectedTx?.currency || profile?.currency || "PKR"}
      />
    </MerchantLayout>
  );
}
